import { useState, useEffect, useRef } from "react";
import { registerDropHandler } from "./dragDropBus";

/**
 * Subscribes to the global drag-drop bus.
 * `onFileDrop` is called when files are dropped — but only if this is the
 * most recently registered hook instance (i.e. the "topmost" component),
 * preventing duplicate uploads when multiple components are mounted.
 * Returns `isDraggingOver` so the caller can show a full-screen overlay.
 */
export function useGlobalDragDrop(onFileDrop: (files: File[]) => void) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  // Keep a ref so the bus closure always calls the latest callback without
  // needing to re-register on every render.
  const callbackRef = useRef(onFileDrop);
  callbackRef.current = onFileDrop;

  useEffect(() => {
    return registerDropHandler(
      (files) => callbackRef.current(files),
      setIsDraggingOver,
    );
  }, []); // runs once — latest callback always reached via ref

  return { isDraggingOver };
}
